import { useState } from "react";
import { useSettingsRHF } from "../hooks/useSettingsRHF";
import { liabilitiesSchema } from "../types/settingsSchemas";
import type { LiabilitiesSettingsData } from "../types/settings";

/**
 * Field classification for Liabilities form (two-tier visibility)
 *
 * Essential: balance + rate pairs shown during onboarding + Settings
 * Advanced: shown only in Settings (behind ShowMoreToggle)
 */
export const LIABILITIES_ESSENTIAL_FIELDS = [
  "mortgage",
  "mortgageInterestRate",
  "creditCardBalance",
  "creditCardInterestRate",
  "carLoan",
  "carLoanInterestRate",
  "studentLoans",
  "studentLoanInterestRate",
  "lineOfCredit",
  "lineOfCreditInterestRate",
  "otherLiabilities",
  "otherLiabilitiesInterestRate",
] as const;

export const LIABILITIES_ADVANCED_FIELDS = [
  "investmentPropertyMortgage",
  "investmentPropertyMortgageRate",
  "heloc",
  "helocInterestRate",
  "businessLoans",
  "businessLoansInterestRate",
  "taxesPayable",
  "mortgageAmortizationRemaining",
] as const;

/** All balance fields that contribute to the total (excludes rates) */
const BALANCE_FIELDS: (keyof LiabilitiesSettingsData)[] = [
  "mortgage",
  "creditCardBalance",
  "carLoan",
  "lineOfCredit",
  "studentLoans",
  "otherLiabilities",
  "investmentPropertyMortgage",
  "heloc",
  "businessLoans",
  "taxesPayable",
];

/**
 * Shared logic for Liabilities functionality
 * Handles form state, validation, and data management
 */
export function useLiabilitiesLogic() {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const { total: totalLiabilities, ...rest } =
    useSettingsRHF<LiabilitiesSettingsData>(
      "liabilities",
      liabilitiesSchema,
      BALANCE_FIELDS
    );

  const toggleAdvanced = () => setShowAdvanced((prev) => !prev);

  return { ...rest, totalLiabilities, showAdvanced, toggleAdvanced };
}
